import { createSlice } from '@reduxjs/toolkit';
import { arrayToDictionary } from '../../utils/arrayToDictionary';
import {
  copyFormEditorFormThunk,
  deleteFormEditorFormThunk,
  getEmployerBusinessesThunk,
  getFormEditorFormThunk,
  saveFormEditorFormThunk,
  updateFormEditorFormThunk,
} from '../thunks/formEditorThunks';
import { v4 } from 'uuid';

const getNormalizedBusinessIds = (form = {}) => {
  const rawBusinessIds = Array.isArray(form.businessIds)
    ? form.businessIds
    : form.businessId
      ? [form.businessId]
        : [];

  return [...new Set(rawBusinessIds.map((id) => `${id}`.trim()).filter(Boolean))];
};

const normalizeFormEditorForm = (form = {}) => {
  const businessIds = getNormalizedBusinessIds(form);
  const { businessId, ...restForm } = form;

  return {
    ...restForm,
    businessIds,
  };
};

export const formEditorSlice = createSlice({
  name: 'formEditor',
  initialState: {
    isLoading: false,
    selectedFormId: null,
    formEditorForms: {},
    businesses: [],
    selectedInputFieldId: null,
    selectedInputFieldIds: [],
    hasUnsavedChanges: false,
    isAutoSave: false,
    inputFieldOpacity: 0.7,
    uiForShowingForms: 'list',
  },
  reducers: {
    initFormsFromLocalstorage: (state, action) => {
      const formEditorForms = localStorage.getItem('formEditorForms');
      const isAutoSave = localStorage.getItem('isAutoSaveFormEditor');
      state.isAutoSave = isAutoSave;
      const parsedForms = JSON.parse(formEditorForms);
      state.formEditorForms = Object.fromEntries(
        Object.entries(parsedForms || {}).map(([id, form]) => [
          id,
          normalizeFormEditorForm(form),
        ])
      );
    },
    setIsAutoSaveFormEditor: (state, action) => {
      state.isAutoSave = action.payload;
      localStorage.setItem('isAutoSaveFormEditor', action.payload);
    },
    changeUiForShowingForms: (state, action) => {
      if (state.uiForShowingForms === 'grid') {
        state.uiForShowingForms = 'list';
      } else {
        state.uiForShowingForms = 'grid';
      }
    },
    setInputFieldOpacity: (state, action) => {
      state.inputFieldOpacity = action.payload;
    },
    selectForm: (state, action) => {
      state.selectedFormId = action.payload.id;
    },
    unselectForm: (state, action) => {
      state.selectedFormId = null;
      state.selectedInputFieldId = null;
      state.selectedInputFieldIds = [];
    },

    // meaby unneccesary because we call unselectForm when component dismounts
    createNewForm: (state, action) => {
      state.selectedFormId = null;
      state.selectedInputFieldId = null;
    },

    addInputField: (state, action) => {
      const { inputField, pageIndex = 0 } = action.payload;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      // Ensure inputFields is an array of arrays
      if (!Array.isArray(selectedFormEditorForm.inputFields)) {
        selectedFormEditorForm.inputFields = [];
      }

      // Ensure the page array exists
      if (!selectedFormEditorForm.inputFields[pageIndex]) {
        selectedFormEditorForm.inputFields[pageIndex] = [];
      }

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      updatedInputFields[pageIndex] = [
        ...updatedInputFields[pageIndex],
        inputField,
      ];

      const updatedSelectedFormEditorForm = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };

      state.formEditorForms[updatedSelectedFormEditorForm.id] =
        updatedSelectedFormEditorForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    addNameForTheForm: (state, action) => {
      const name = action.payload;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const updatedForm = {
        ...selectedFormEditorForm,
        name,
      };

      state.formEditorForms[updatedForm.id] = updatedForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    updateBusinessIdForTheForm: (state, action) => {
      const businessIdToAdd = `${action.payload || ''}`.trim();

      if (!businessIdToAdd) return;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];
      const existingBusinessIds = getNormalizedBusinessIds(
        selectedFormEditorForm
      );

      if (existingBusinessIds.includes(businessIdToAdd)) return;

      const updatedForm = normalizeFormEditorForm({
        ...selectedFormEditorForm,
        businessIds: [...existingBusinessIds, businessIdToAdd],
      });

      state.formEditorForms[updatedForm.id] = updatedForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },
    updateBusinessIdsForTheForm: (state, action) => {
      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];
      const nextBusinessIds = Array.isArray(action.payload)
        ? action.payload
        : [];

      const updatedForm = normalizeFormEditorForm({
        ...selectedFormEditorForm,
        businessIds: nextBusinessIds,
      });

      state.formEditorForms[updatedForm.id] = updatedForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },
    removeBusinessIdForTheForm: (state, action) => {
      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];
      const businessIdToRemove = `${action.payload || ''}`.trim();
      const existingBusinessIds = getNormalizedBusinessIds(
        selectedFormEditorForm
      );

      const updatedForm = normalizeFormEditorForm({
        ...selectedFormEditorForm,
        businessIds: existingBusinessIds.filter(
          (id) => id !== businessIdToRemove
        ),
      });

      state.formEditorForms[updatedForm.id] = updatedForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    selectInputField: (state, action) => {
      state.selectedInputFieldId = action.payload.id;
      state.selectedInputFieldIds = [action.payload.id];
    },
    toggleSelectInputField: (state, action) => {
      const id = action.payload.id;
      const idx = state.selectedInputFieldIds.indexOf(id);
      if (idx === -1) {
        state.selectedInputFieldIds.push(id);
        state.selectedInputFieldId = id;
      } else {
        state.selectedInputFieldIds.splice(idx, 1);
        if (state.selectedInputFieldId === id) {
          state.selectedInputFieldId =
            state.selectedInputFieldIds.length > 0
              ? state.selectedInputFieldIds[state.selectedInputFieldIds.length - 1]
              : null;
        }
      }
    },
    shiftSelectInputFields: (state, action) => {
      const { id, pageIndex } = action.payload;
      const selectedFormEditorForm = state.formEditorForms[state.selectedFormId];
      if (!selectedFormEditorForm?.inputFields?.[pageIndex]) return;

      const pageFields = selectedFormEditorForm.inputFields[pageIndex];
      const anchorId = state.selectedInputFieldId;
      if (!anchorId) {
        state.selectedInputFieldId = id;
        state.selectedInputFieldIds = [id];
        return;
      }

      const anchorField = pageFields.find((f) => f.id === anchorId);
      const targetField = pageFields.find((f) => f.id === id);
      if (!anchorField || !targetField) return;

      const minY = Math.min(anchorField.y, targetField.y);
      const maxY = Math.max(anchorField.y, targetField.y);
      const xTolerance = 10;

      // Select all fields in the same column (similar X) between anchor and target Y
      const columnFields = pageFields.filter(
        (f) =>
          Math.abs(f.x - anchorField.x) <= xTolerance &&
          f.y >= minY &&
          f.y <= maxY
      );

      const newIds = columnFields.map((f) => f.id);
      // Merge with existing selection
      const merged = [...new Set([...state.selectedInputFieldIds, ...newIds])];
      state.selectedInputFieldIds = merged;
      state.selectedInputFieldId = id;
    },
    unSelectInputField: (state, action) => {
      state.selectedInputFieldId = null;
      state.selectedInputFieldIds = [];
    },
    equalizeSelectedRowGaps: (state, action) => {
      const { pageIndex } = action.payload;
      const selectedFormEditorForm = state.formEditorForms[state.selectedFormId];
      if (!selectedFormEditorForm?.inputFields?.[pageIndex]) return;

      const pageFields = selectedFormEditorForm.inputFields[pageIndex];
      const selectedFields = pageFields.filter((f) =>
        state.selectedInputFieldIds.includes(f.id)
      );
      if (selectedFields.length < 2) return;

      // Group selected fields into rows by Y position (tolerance of 5px)
      const rows = [];
      const sorted = [...selectedFields].sort((a, b) => a.y - b.y);
      for (const field of sorted) {
        const existingRow = rows.find((r) => Math.abs(r.y - field.y) <= 5);
        if (existingRow) {
          existingRow.fields.push(field);
        } else {
          rows.push({ y: field.y, fields: [field] });
        }
      }

      if (rows.length < 2) return;

      rows.sort((a, b) => a.y - b.y);

      const firstRowY = rows[0].y;
      const lastRowY = rows[rows.length - 1].y;
      const exactSpacing = (lastRowY - firstRowY) / (rows.length - 1);

      const fieldUpdates = {};
      rows.forEach((row, rowIndex) => {
        // Round each position individually to keep first and last rows pinned
        const newY = Math.round(firstRowY + rowIndex * exactSpacing);
        const yDelta = newY - row.y;
        for (const field of row.fields) {
          fieldUpdates[field.id] = field.y + yDelta;
        }
      });

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      updatedInputFields[pageIndex] = updatedInputFields[pageIndex].map(
        (field) => {
          if (fieldUpdates[field.id] !== undefined) {
            return { ...field, y: fieldUpdates[field.id] };
          }
          return field;
        }
      );

      state.formEditorForms[state.selectedFormId] = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },
    updateSelectedInputFields: (state, action) => {
      const { pageIndex, updates } = action.payload;
      const selectedFormEditorForm = state.formEditorForms[state.selectedFormId];
      if (!selectedFormEditorForm?.inputFields?.[pageIndex]) return;

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      updatedInputFields[pageIndex] = updatedInputFields[pageIndex].map((field) => {
        if (state.selectedInputFieldIds.includes(field.id)) {
          return { ...field, ...updates };
        }
        return field;
      });

      state.formEditorForms[state.selectedFormId] = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },
    copySelectedInputFields: (state, action) => {
      const { pageIndex } = action.payload;
      const selectedFormEditorForm = state.formEditorForms[state.selectedFormId];
      if (!selectedFormEditorForm?.inputFields?.[pageIndex]) return;

      const pageFields = selectedFormEditorForm.inputFields[pageIndex];
      const selectedFields = pageFields.filter((f) =>
        state.selectedInputFieldIds.includes(f.id)
      );
      if (selectedFields.length === 0) return;

      // Detect actual row-to-row spacing from existing fields on the page.
      // For each selected field, find the nearest field directly above it
      // at a similar x position. This avoids cumulative drift when the
      // field height doesn't exactly match the PDF's row spacing.
      let detectedSpacing = null;
      for (const field of selectedFields) {
        const fieldsAbove = pageFields.filter(
          (f) =>
            !state.selectedInputFieldIds.includes(f.id) &&
            Math.abs(f.x - field.x) < 10 &&
            f.y < field.y
        );
        if (fieldsAbove.length > 0) {
          const closest = fieldsAbove.reduce((prev, curr) =>
            field.y - curr.y < field.y - prev.y ? curr : prev
          );
          detectedSpacing = field.y - closest.y;
          break;
        }
      }

      const yOffset = detectedSpacing || selectedFields[0].height || 30;

      const maxIndex = Math.max(
        0,
        ...pageFields.map((f) => f.originalIndex || 0)
      );

      const copies = selectedFields.map((field, i) => ({
        ...field,
        id: v4(),
        y: field.y + yOffset,
        originalIndex: maxIndex + i + 1,
      }));

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      updatedInputFields[pageIndex] = [...pageFields, ...copies];

      state.formEditorForms[state.selectedFormId] = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };
      state.hasUnsavedChanges = true;

      // Select the newly copied fields
      state.selectedInputFieldIds = copies.map((c) => c.id);
      state.selectedInputFieldId = copies[copies.length - 1].id;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },
    deleteSelectedInputFields: (state, action) => {
      const { pageIndex } = action.payload;
      const selectedFormEditorForm = state.formEditorForms[state.selectedFormId];
      if (!selectedFormEditorForm?.inputFields?.[pageIndex]) return;

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      updatedInputFields[pageIndex] = updatedInputFields[pageIndex].filter(
        (field) => !state.selectedInputFieldIds.includes(field.id)
      );

      state.formEditorForms[state.selectedFormId] = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };
      state.hasUnsavedChanges = true;
      state.selectedInputFieldId = null;
      state.selectedInputFieldIds = [];

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },
    deleteInputField: (state, action) => {
      const { id, pageIndex } = action.payload;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      if (updatedInputFields[pageIndex]) {
        updatedInputFields[pageIndex] = updatedInputFields[pageIndex].filter(
          (field) => field.id !== id
        );
      }

      const updatedSelectedFormEditorForm = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };

      state.formEditorForms[updatedSelectedFormEditorForm.id] =
        updatedSelectedFormEditorForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
      state.selectedInputFieldId = null;
    },
    copyInputField: (state, action) => {
      const { id, pageIndex } = action.payload;

      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const selectedInputField = selectedFormEditorForm.inputFields[
        pageIndex
      ]?.find((field) => field.id === id);

      if (!selectedInputField) return;

      const copyOfSelectedInputField = {
        ...selectedInputField,
        id: v4(),
        x: selectedInputField.x + 100,
      };

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      if (!updatedInputFields[pageIndex]) {
        updatedInputFields[pageIndex] = [];
      }
      updatedInputFields[pageIndex] = [
        ...updatedInputFields[pageIndex],
        copyOfSelectedInputField,
      ];

      const updatedSelectedFormEditorForm = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };

      state.formEditorForms[updatedSelectedFormEditorForm.id] =
        updatedSelectedFormEditorForm;

      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );

      state.selectedInputFieldId = null;
    },

    onInputFieldChange: (state, action) => {
      const { updatedInputField, pageIndex = 0 } = action.payload;
      let selectedFormEditorForm = state.formEditorForms[state.selectedFormId];

      const updatedInputFields = [...selectedFormEditorForm.inputFields];
      const pageFields = [...updatedInputFields[pageIndex]];
      const fieldIndex = pageFields.findIndex(
        (field) => field.id === updatedInputField.id
      );

      if (fieldIndex !== -1) {
        pageFields[fieldIndex] = updatedInputField;
        updatedInputFields[pageIndex] = pageFields;
      }

      const updatedSelectedFormEditorForm = {
        ...selectedFormEditorForm,
        inputFields: updatedInputFields,
      };

      state.formEditorForms[updatedSelectedFormEditorForm.id] =
        updatedSelectedFormEditorForm;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    formUpdateAfterTextract: (state, action) => {
      const formEditorForm = action.payload;

      state.formEditorForms = {
        ...state.formEditorForms,
        [formEditorForm.id]: formEditorForm,
      };

      state.selectedFormId = formEditorForm.id;
      state.hasUnsavedChanges = true;

      localStorage.setItem(
        'formEditorForms',
        JSON.stringify(state.formEditorForms)
      );
    },

    setFormEditorFormsFromLocalstorage: (state, action) => {
      const formEditorForms = action.payload;
      state.formEditorForms = Object.fromEntries(
        Object.entries(formEditorForms || {}).map(([id, form]) => [
          id,
          normalizeFormEditorForm(form),
        ])
      );
    },
  },
  extraReducers(builder) {
    builder
      // GET
      .addCase(getFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getFormEditorFormThunk.fulfilled, (state, action) => {
        const formEditorForms = (action.payload || []).map(
          normalizeFormEditorForm
        );
        state.formEditorForms = arrayToDictionary(formEditorForms);
        state.isLoading = false;
        localStorage.setItem(
          'formEditorForms',
          JSON.stringify(state.formEditorForms)
        );
      })
      .addCase(getFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      })

      // CREATE
      .addCase(saveFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(saveFormEditorFormThunk.fulfilled, (state, action) => {
        const formEditorFormDraft = normalizeFormEditorForm(action.payload);

        state.formEditorForms = {
          ...state.formEditorForms,
          [formEditorFormDraft.id]: formEditorFormDraft,
        };
        localStorage.setItem(
          'formEditorForms',
          JSON.stringify(state.formEditorForms)
        );

        state.selectedFormId = formEditorFormDraft.id;
        state.hasUnsavedChanges = false;
        state.isLoading = false;
      })
      .addCase(saveFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      })

      // UPDATE
      .addCase(updateFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(updateFormEditorFormThunk.fulfilled, (state, action) => {
        const formEditorForm = normalizeFormEditorForm(action.payload);

        state.formEditorForms = {
          ...state.formEditorForms,
          [formEditorForm.id]: formEditorForm,
        };
        state.hasUnsavedChanges = false;

        localStorage.setItem(
          'formEditorForms',
          JSON.stringify(state.formEditorForms)
        );
      })
      .addCase(updateFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      })

      // EMPLOYER GET
      .addCase(getEmployerBusinessesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.businesses = action.payload;
      })
      .addCase(getEmployerBusinessesThunk.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(getEmployerBusinessesThunk.pending, (state, action) => {
        state.isLoading = true;
      })

      // COPY
      .addCase(copyFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(copyFormEditorFormThunk.fulfilled, (state, action) => {
        const formCopy = normalizeFormEditorForm(action.payload);

        state.formEditorForms = {
          ...state.formEditorForms,
          [formCopy.id]: formCopy,
        };
        state.isLoading = false;
      })
      .addCase(copyFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      })
      // DELETE
      .addCase(deleteFormEditorFormThunk.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(deleteFormEditorFormThunk.fulfilled, (state, action) => {
        const formId = action.payload.id;

        delete state.formEditorForms[formId];
        state.isLoading = false;
      })
      .addCase(deleteFormEditorFormThunk.rejected, (state, action) => {
        state.isLoading = false;
      });
  },
});

// ACTIONS
export const {
  initFormsFromLocalstorage,
  addInputField,
  selectInputField,
  toggleSelectInputField,
  shiftSelectInputFields,
  unSelectInputField,
  onInputFieldChange,
  setInputFields,
  textractGeneratedFields,
  setFormEditorInputFieldsFromLocalstorage,
  deleteInputField,
  copyInputField,
  copySelectedInputFields,
  deleteSelectedInputFields,
  updateSelectedInputFields,
  equalizeSelectedRowGaps,
  createNewForm,
  formUpdateAfterTextract,
  selectForm,
  unselectForm,
  addNameForTheForm,
  setIsAutoSaveFormEditor,
  setInputFieldOpacity,
  updateBusinessIdForTheForm,
  updateBusinessIdsForTheForm,
  removeBusinessIdForTheForm,
  changeUiForShowingForms,
} = formEditorSlice.actions;

// SELECTORS

export const getFormEditorControlData = (state) => {
  const slice = state.formEditorSlice;

  return {
    uiForShowingForms: slice?.uiForShowingForms,
    isLoading: slice?.isLoading,
    hasUnsavedChanges: slice?.hasUnsavedChanges,
    selectedFormId: slice?.selectedFormId,
    formEditorForms: Object.values(slice?.formEditorForms),
    selectedInputFieldId: slice?.selectedInputFieldId,
    selectedInputFieldIds: slice?.selectedInputFieldIds || [],
    inputFieldOpacity: slice?.inputFieldOpacity,
    selectedInputField: (() => {
      if (!slice?.selectedInputFieldId || !slice?.selectedFormId) return null;
      const form = slice.formEditorForms[slice.selectedFormId];
      if (!form?.inputFields) return null;

      // Search through all pages to find the selected field
      for (const pageFields of form.inputFields) {
        if (Array.isArray(pageFields)) {
          const field = pageFields.find(
            (field) => field.id === slice.selectedInputFieldId
          );
          if (field) return field;
        }
      }
      return null;
    })(),
  };
};

export const getUnpublishedFormEditorForms = (state) => {
  const slice = state.formEditorSlice;
  const formEditorForms = Object.values(slice?.formEditorForms);

  const unpublished = formEditorForms.filter((form) => {
    return getNormalizedBusinessIds(form).length === 0;
  });

  return unpublished;
};

export const getPublishedFormEditorForms = (state) => {
  const slice = state.formEditorSlice;
  const formEditorForms = Object.values(slice?.formEditorForms);

  const published = formEditorForms?.filter((form) => {
    return getNormalizedBusinessIds(form).length > 0;
  });

  return published;
};

export const getSelectedFormEditorForm = (state) => {
  const slice = state.formEditorSlice;

  if (!slice?.selectedFormId) return null;
  const formEditorForm = slice?.formEditorForms[slice.selectedFormId];

  return {
    ...normalizeFormEditorForm(formEditorForm),
    inputFields: formEditorForm?.inputFields || [],
  };
};

export const getFormEditorIsAutoSave = (state) => {
  const slice = state.formEditorSlice;
  return slice.isAutoSave;
};

// input fields are dictionary here
export const getSelectedFormEditorFormForDynamoDB = (state) => {
  const slice = state.formEditorSlice;

  if (!slice?.selectedFormId) return null;
  const formEditorForm = slice.formEditorForms[slice.selectedFormId];
  return normalizeFormEditorForm(formEditorForm);
};

export const getEmployerBusinesses = (state) =>
  state.formEditorSlice.businesses;
